import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Product, CartItem, Order, OrderCreatePayload } from '../types';
import toast from 'react-hot-toast';
import { createOrder as apiCreateOrder, getProducts } from '../utils/api';
import { AuthContext } from './AuthContext';
import { getProductUnitPrice, getCasePrice } from '../utils/pricing';
import { createCartItemId, getTotalItemCount, calculateCustomCasePrice, CUSTOM_CASE_FEE_CENTS } from '../utils/caseQuantity';

// Local storage cart item structure (updated for case quantities)
interface LocalCartItem {
  productId: number;
  caseQuantity: number;
  numberOfCases: number;
  isCustomCase?: boolean;
  product: Product;
}

interface CartContextType {
  cart: { items: LocalCartItem[] } | null;
  loading: boolean;
  addToCart: (productId: number, caseQuantity: number, numberOfCases: number, showToast?: boolean, isCustomCase?: boolean) => Promise<void>;
  removeFromCart: (productId: number, caseQuantity: number) => Promise<void>;
  updateCartItemQuantity: (productId: number, caseQuantity: number, numberOfCases: number) => Promise<void>;
  getCartTotal: () => number;
  createOrder: (payload: OrderCreatePayload) => Promise<Order>;
  clearCart: () => void;
}

export const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'sharewares_cart';

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<{ items: LocalCartItem[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const authContext = useContext(AuthContext);

  // Load products for cart functionality
  const fetchProducts = useCallback(async () => {
    if (!authContext?.user) {
      console.log("User not authenticated, skipping product fetch.");
      setProducts([]); // Clear products if user logs out or is not authenticated
      return;
    }
    try {
      const productData = await getProducts();
      setProducts(productData);
    } catch (error) {
      console.error("Failed to fetch products", error);
    }
  }, [authContext?.user]); // Add authContext.user to dependencies

  // Load cart from session storage
  const loadCartFromStorage = useCallback(() => {
    try {
      const storedCart = sessionStorage.getItem(CART_STORAGE_KEY);
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        setCart(parsedCart);
      } else {
        setCart({ items: [] });
      }
    } catch (error) {
      console.error("Failed to load cart from storage", error);
      setCart({ items: [] });
    }
  }, []);

  // Save cart to session storage
  const saveCartToStorage = useCallback((cartData: { items: LocalCartItem[] }) => {
    try {
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    } catch (error) {
      console.error("Failed to save cart to storage", error);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    loadCartFromStorage();
  }, [fetchProducts, loadCartFromStorage]);

  const addToCart = async (productId: number, caseQuantity: number, numberOfCases: number, showToast: boolean = true, isCustomCase: boolean = false) => {
    console.log(`Attempting to add product ID: ${productId} with case quantity: ${caseQuantity}, number of cases: ${numberOfCases}, isCustomCase: ${isCustomCase}`);
    if (!authContext?.user) {
      console.error("User not authenticated when trying to add to cart.");
      throw new Error("User not authenticated");
    }
    
    console.log("Current products in state:", products.map(p => ({ id: p.id, name: p.name })));
    const product = products.find(p => p.id === productId);
    if (!product) {
      console.error(`Product with ID ${productId} not found in fetched products.`);
      toast.error('Product not found');
      return;
    }

    // Use functional state update to ensure we get the latest cart state
    setCart(prevCart => {
      const currentCart = prevCart || { items: [] };
      const cartItemId = createCartItemId(productId, caseQuantity);
      const existingItemIndex = currentCart.items.findIndex(
        item => createCartItemId(item.productId, item.caseQuantity) === cartItemId
      );

      let newCart;
      if (existingItemIndex >= 0) {
        // Update existing item (add to number of cases)
        newCart = {
          ...currentCart,
          items: currentCart.items.map((item, index) => 
            index === existingItemIndex 
              ? { ...item, numberOfCases: item.numberOfCases + numberOfCases }
              : item
          )
        };
      } else {
        // Add new item
        newCart = {
          ...currentCart,
          items: [...currentCart.items, { productId, caseQuantity, numberOfCases, isCustomCase, product }]
        };
      }

      // Save to storage with the new cart
      saveCartToStorage(newCart);
      return newCart;
    });
    
    if (showToast) {
      const totalItems = getTotalItemCount(caseQuantity, numberOfCases);
      const customText = isCustomCase ? ' (Custom Case)' : '';
      toast.success(`Added ${totalItems} items${customText} to cart!`);
    }
  };

  const removeFromCart = async (productId: number, caseQuantity: number) => {
    if (!authContext?.user) throw new Error("User not authenticated");
    
    const currentCart = cart || { items: [] };
    const cartItemId = createCartItemId(productId, caseQuantity);
    const newCart = {
      ...currentCart,
      items: currentCart.items.filter(
        item => createCartItemId(item.productId, item.caseQuantity) !== cartItemId
      )
    };

    setCart(newCart);
    saveCartToStorage(newCart);
    toast.success('Item removed from cart!');
  };

  const updateCartItemQuantity = async (productId: number, caseQuantity: number, numberOfCases: number) => {
    if (!authContext?.user) throw new Error("User not authenticated");
    
    const currentCart = cart || { items: [] };
    
    if (numberOfCases <= 0) {
      await removeFromCart(productId, caseQuantity);
      return;
    }

    const cartItemId = createCartItemId(productId, caseQuantity);
    const newCart = {
      ...currentCart,
      items: currentCart.items.map(item => 
        createCartItemId(item.productId, item.caseQuantity) === cartItemId
          ? { ...item, numberOfCases }
          : item
      )
    };

    setCart(newCart);
    saveCartToStorage(newCart);
  };

  const getCartTotal = (): number => {
    if (!cart) return 0;
    return cart.items.reduce((total: number, item: LocalCartItem) => {
      let casePrice: number;
      if (item.isCustomCase) {
        // Calculate custom case price including custom fee
        casePrice = calculateCustomCasePrice(item.product, item.caseQuantity);
      } else {
        // Standard case price
        casePrice = getCasePrice(item.product, item.caseQuantity);
      }
      return total + casePrice * item.numberOfCases;
    }, 0);
  };

  const createOrder = async (payload: OrderCreatePayload): Promise<Order> => {
    if (!authContext?.user) throw new Error("User not authenticated");
    const newOrder = await apiCreateOrder(payload);
    
    // Clear cart after successful order
    const emptyCart = { items: [] };
    setCart(emptyCart);
    saveCartToStorage(emptyCart);
    
    return newOrder;
  };

  const clearCart = () => {
    const emptyCart = { items: [] };
    setCart(emptyCart);
    saveCartToStorage(emptyCart);
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    getCartTotal,
    createOrder,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 